/**
 * The Hub's Tracking assets panel (decision 420, Q363 A; docs/specs/portal/operations-hub.md §19), as Teslyn Field's
 * list (`apps/field/src/tracker/TrackingAssetsList.tsx`; Diurnal parity 03-039): the organisation's own aircraft and
 * vehicles only, those needing attention first, each with its tracker colour, state in words, tracker badge,
 * altitude, speed, heading, flight times and age; the history from 1 to 48 hours; fleet groups; pressing a name flies
 * the map to it, "Follow" keeps it in view. The list's height can be dragged and is remembered in this browser.
 * Rules and words: `@teslyn/domain` `hubTracking`, `trackingList`, `aircraftSourceLegend`.
 */
import { useEffect, useId, useRef, type ReactElement } from 'react';
import {
  aircraftSourceLegend,
  type AircraftColours,
  type AircraftPosition,
  type TrackingList,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';

const HEIGHT_KEY = 'teslyn.hub.trackingHeight';

function rememberedHeight(): string | undefined {
  try {
    const v = localStorage.getItem(HEIGHT_KEY);
    return v && /^\d{2,4}px$/.test(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

export interface TrackingAssetsPanelProps {
  readonly list: TrackingList | null;
  readonly aircraft: readonly AircraftPosition[];
  readonly colours: ReadonlyMap<string, AircraftColours>;
  readonly groups: readonly string[];
  readonly shownGroups: readonly string[];
  readonly onGroups: (groups: string[]) => void;
  readonly hours: number;
  readonly onHours: (hours: number) => void;
  readonly follow: string | null;
  readonly onFollow: (id: string | null) => void;
  readonly onLocate: (id: string) => void;
  /** Loading, an error, or null. */
  readonly status: string | null;
  readonly followStopped: string | null;
  /** The cloud poller's words per tracker account. */
  readonly accounts: readonly { readonly state: string; readonly message: string }[];
  /** Public air traffic on the map (parity C7): off, on, or the poller's words; null says nothing. */
  readonly traffic?: string | null;
}

export function TrackingAssetsPanel(p: TrackingAssetsPanelProps): ReactElement {
  const box = useRef<HTMLDivElement>(null);
  const historyId = useId();
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const o = new ResizeObserver(() => {
      if (!el.style.height) return;
      try {
        localStorage.setItem(HEIGHT_KEY, el.style.height);
      } catch {
        /* storage unavailable: the height is not remembered */
      }
    });
    o.observe(el);
    return () => o.disconnect();
  }, []);
  const height = rememberedHeight();
  const byId = new Map(p.aircraft.map((a) => [a.id, a]));
  const legend = aircraftSourceLegend(p.aircraft);
  return (
    <div
      ref={box}
      className="hub-tracking"
      role="region"
      aria-label="Tracking assets"
      {...(height ? { style: { height } } : {})}
    >
      <h2 className="label hub-tracking__title">{p.list?.title ?? 'Tracking assets'}</h2>
      <div className="hub-tracking__history">
        <label className="caption" htmlFor={historyId}>
          History: <span className="data">{p.hours} h</span>
        </label>
        <input
          id={historyId}
          type="range"
          min={1}
          max={48}
          step={1}
          value={p.hours}
          onChange={(e) => p.onHours(Number(e.target.value))}
        />
      </div>
      {p.groups.length > 0 && (
        <fieldset className="hub-tracking__groups">
          <legend className="caption muted">Fleet groups (none ticked shows all)</legend>
          {p.groups.map((g) => (
            <label key={g} className="caption hub-tracking__group">
              <input
                type="checkbox"
                checked={p.shownGroups.includes(g)}
                onChange={(e) =>
                  p.onGroups(e.target.checked ? [...p.shownGroups, g] : p.shownGroups.filter((x) => x !== g))
                }
              />{' '}
              {g}
            </label>
          ))}
        </fieldset>
      )}
      {p.followStopped && (
        <p className="caption" role="status">
          Stopped following {p.followStopped}: you moved the map.
        </p>
      )}
      {p.status && <p className="caption muted">{p.status}</p>}
      {p.list && p.list.rows.length === 0 && !p.status && (
        <p className="caption muted">
          No tracker positions in the last {p.hours} h. Positions show here once Teslyn Cloud stores your
          organisation&apos;s tracker positions.
        </p>
      )}
      {p.list && p.list.rows.length > 0 && (
        <ul className="hub-tracking__list" aria-label="Aircraft and vehicles">
          {p.list.rows.map((r) => {
            const followed = p.follow === r.id;
            return (
              <li
                key={r.id}
                className={`caption hub-tracking__row${followed ? ' hub-tracking__row--on' : ''}`}
              >
                <div className="hub-tracking__line">
                  <span
                    className="hub-swatch"
                    style={{ background: `var(--${p.colours.get(r.id)?.trail ?? 'accent'})` }}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className="hub-tracking__name data"
                    onClick={() => byId.get(r.id) && p.onLocate(r.id)}
                    aria-label={`Show ${r.label} on the map`}
                  >
                    {r.label}
                  </button>
                  <abbr title={r.source} className="hub-tracking__badge">
                    {r.badge}
                  </abbr>
                  {r.rfds && <span>RFDS</span>}
                  <span className={r.alarm ? 'hub-tracking__state--fail' : ''}>{r.stateWord}</span>
                  <Button
                    variant="quiet"
                    aria-pressed={followed}
                    onClick={() => p.onFollow(followed ? null : r.id)}
                  >
                    {followed ? 'Stop following' : 'Follow'}
                  </Button>
                </div>
                <div className="hub-tracking__details muted">
                  {[r.link, r.motion, r.flight, r.age].filter(Boolean).join(' · ')}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {p.accounts.length > 0 && (
        <ul className="hub-tracking__accounts" aria-label="Tracker accounts">
          {p.accounts.map((a, i) => (
            <li key={i} className={`caption${a.state === 'ok' ? ' muted' : ''}`}>
              {a.message}
            </li>
          ))}
        </ul>
      )}
      {p.traffic && (
        <p className="caption muted" role="note" aria-label="Public air traffic">
          {p.traffic}
        </p>
      )}
      {legend.length > 0 && (
        <ul className="legend hub-tracking__legend" aria-label="Tracker colours">
          {legend.map((l) => (
            <li key={l.source} className="legend__item caption">
              <span className="hub-swatch" style={{ background: `var(--${l.colour})` }} aria-hidden="true" />
              {l.word}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
